import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Link, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import PageContainer from '../../components/layout/PageContainer';
import { getPOIWithRelations } from '../../services/entityService';
import { MESSAGES } from '../../constants/messages';

const POIDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [poi, setPoi] = React.useState<any>(null);
    const [organizations, setOrganizations] = React.useState<any[]>([]);
    const [vcFirms, setVCFirms] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!id) return;
        const fetchPOIAndRelations = async () => {
            try {
                const { poi, organizations, vcFirms } = await getPOIWithRelations(id);
                setPoi(poi);
                setOrganizations(organizations);
                setVCFirms(vcFirms);
            } catch (err) {
                setError('Failed to fetch person of interest details.');
            } finally {
                setLoading(false);
            }
        };
        fetchPOIAndRelations();
    }, [id]);

    const handleBack = () => {
        navigate(-1);
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh"><CircularProgress /></Box>;
    }
    if (error) {
        return <Typography color="error">{error}</Typography>;
    }
    if (!poi) {
        return <Typography>No person of interest found.</Typography>;
    }

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button 
                    startIcon={<ArrowBack />} 
                    onClick={handleBack}
                    sx={{ mr: 2 }}
                >
                    Back
                </Button>
                <Typography variant="h4">
                    {poi.name}
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {MESSAGES.PAGE_DISCLAIMER}
            </Typography>
            {poi.name_alias && <Typography><strong>Also known as:</strong> {poi.name_alias}</Typography>}
            {poi.description && <Typography><strong>Description:</strong> {poi.description}</Typography>}
            {poi.investment_theme && <Typography><strong>Investment Focus:</strong> {poi.investment_theme}</Typography>}
            {poi.website && (
                <Typography><strong>Website:</strong> <Link href={poi.website} target="_blank" rel="noopener noreferrer">{poi.website}</Link></Typography>
            )}
            {poi.websites && poi.websites.length > 0 && (
                <Typography><strong>Websites:</strong> {poi.websites.map((site: string, index: number) => (
                    <span key={index}>
                        <Link href={site} target="_blank" rel="noopener noreferrer">{site}</Link>
                        {index < poi.websites.length - 1 && ', '}
                    </span>
                ))}</Typography>
            )}
            {poi.location && <Typography><strong>Location:</strong> {poi.location}</Typography>}
            {poi.industries && poi.industries.length > 0 && <Typography><strong>Industries:</strong> {poi.industries.join(', ')}</Typography>}
            {poi.social_media && poi.social_media.length > 0 && (
                <Typography><strong>Social Media:</strong> {poi.social_media.map((social: string, index: number) => (
                    <span key={index}>
                        <Link href={social} target="_blank" rel="noopener noreferrer">{social}</Link>
                        {index < poi.social_media.length - 1 && ', '}
                    </span>
                ))}</Typography>
            )}
            {poi.contact_emails && poi.contact_emails.length > 0 && (
                <Typography><strong>Contact Emails:</strong> {poi.contact_emails.map((email: string, index: number) => (
                    <span key={index}>
                        <Link href={`mailto:${email}`}>{email}</Link>
                        {index < poi.contact_emails.length - 1 && ', '}
                    </span>
                ))}</Typography>
            )}
            {poi.history_in_IDF && <Typography><strong>Military History:</strong> {poi.history_in_IDF}</Typography>}

            <Box mt={4}>
                <Typography variant="h6" gutterBottom>VC Firms Linked to this Person</Typography>
                {vcFirms && vcFirms.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Position</TableCell>
                                    <TableCell>Investment Theme</TableCell>
                                    <TableCell>Main HQ</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {vcFirms.map((vc: any) => (
                                    <TableRow key={vc.id}>
                                        <TableCell>
                                            <Link href={`/vc/${vc.id}`}>{vc.name}</Link>
                                        </TableCell>
                                        <TableCell>{vc.position || 'N/A'}</TableCell>
                                        <TableCell>{vc.investment_theme}</TableCell>
                                        <TableCell>{vc.main_hq}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography>{MESSAGES.NO_VC_FIRMS_FOUND}</Typography>
                )}
            </Box>

            <Box mt={4}>
                <Typography variant="h6" gutterBottom>Organizations Linked to this Person</Typography>
                {organizations && organizations.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Position</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Main HQ</TableCell>
                                    <TableCell>Operations in Israel</TableCell>
                                    <TableCell>Unit 8200</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {organizations.map((organization: any) => (
                                    <TableRow key={organization.id}>
                                        <TableCell>
                                            <Link href={`/organization/${organization.id}`}>{organization.name}</Link>
                                        </TableCell>
                                        <TableCell>{organization.position || 'N/A'}</TableCell>
                                        <TableCell>{organization.industry}</TableCell>
                                        <TableCell>{organization.main_hq}</TableCell>
                                        <TableCell>{organization.has_operations_in_israel ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>{organization.has_8200_connections ? 'Yes' : 'No'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography>{MESSAGES.NO_ORGANIZATIONS_FOUND}</Typography>
                )}
            </Box>
        </PageContainer>
    );
};

export default POIDetail;
